import { type ReactNode, useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ClipboardList, Wrench } from 'lucide-react';
import { AGENT_CATALOG, type GateMode, type ProjectAutonomy } from '@aiai/shared';
import {
  fetchProject,
  fetchProjectIssuesBoard,
  fetchProjectLoops,
  setProjectAutonomy,
  setProjectGateMode,
  updateProject,
} from '../api';
import { qk } from '../api/queryKeys';
import { invalidatePrototype } from '../api/prototypeCache';
import { useCapabilities } from '../hooks/useCapabilities';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { loopsHref, newIssueHref } from '../lib/projectNav';
import { Callout } from './Callout';

const ICONS: Record<string, ReactNode> = {
  planner: <ClipboardList className="h-4 w-4" aria-hidden="true" />,
  worker: <Wrench className="h-4 w-4" aria-hidden="true" />,
};

const GATE_MODE_OPTIONS: ReadonlyArray<{
  value: GateMode;
  label: string;
  detail: string;
}> = [
  {
    value: 'strict',
    label: 'Strict',
    detail: 'Fail the run when tests keep failing — no PR is opened.',
  },
  {
    value: 'warn',
    label: 'Warn',
    detail: 'Open the PR with a warning even when tests did not pass — you review instead of blocking.',
  },
];

const AUTONOMY_OPTIONS: ReadonlyArray<{
  value: ProjectAutonomy;
  label: string;
  detail: string;
}> = [
  {
    value: 'autopilot',
    label: 'Autopilot',
    detail: 'The team runs on its own: the PM plans every ticket on the board, the Engineer builds and ships it. Nothing waits for you. New work comes from tickets you or your users file, or from loops.',
  },
  {
    value: 'manual',
    label: 'Paused',
    detail: 'The team stops picking up work — no plans, builds, or loops start on their own until you resume.',
  },
];

/**
 * The project's team: the Product Manager and the Engineer are always here —
 * like colleagues on a work floor, they aren't staffed on or off. The one
 * process knob is the Autonomy dial: whether the team runs on its own or is paused.
 * Owner-only to change; members see it read-only, matching the API.
 */
export function AgentsPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const canManage = can('project:manage');

  const query = useQuery({
    queryKey: qk.project(projectId),
    queryFn: () => fetchProject(projectId),
  });

  const mutation = useMutation({
    mutationFn: (autonomy: ProjectAutonomy) => setProjectAutonomy(projectId, autonomy),
    onSuccess: (data) => qc.setQueryData(qk.project(projectId), data),
  });

  const gateModeMutation = useMutation({
    mutationFn: (gateMode: GateMode) => setProjectGateMode(projectId, gateMode),
    onSuccess: (data) => qc.setQueryData(qk.project(projectId), data),
  });

  const defaultBranchMutation = useMutation({
    mutationFn: (defaultBranch: string) => updateProject(projectId, { defaultBranch }),
    onSuccess: (data) => qc.setQueryData(qk.project(projectId), data),
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => updateProject(projectId, { name }),
    onSuccess: (data) => {
      qc.setQueryData(qk.project(projectId), data);
      // Pages key the project by the route ref (code or UUID); refresh every
      // cached copy plus the lists/breadcrumbs that show the name.
      void qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'project' && q.queryKey.length === 2,
      });
      void qc.invalidateQueries({ queryKey: qk.projects });
      void qc.invalidateQueries({ queryKey: qk.myProjects });
      void qc.invalidateQueries({ queryKey: qk.prototypes });
      void qc.invalidateQueries({ queryKey: qk.myPrototypes });
      void invalidatePrototype(qc, data.id);
    },
  });

  const autonomy = query.data?.autonomy;
  const isAutopilot = autonomy === 'autopilot';

  // Autopilot only works the board (plus whatever loops file) — it never
  // invents work. Surface an empty board + no enabled loop as "idle" so an
  // owner doesn't read silence as a broken team.
  const boardCountsQuery = useQuery({
    queryKey: [...qk.projectIssues(projectId), 'activity-counts'] as const,
    queryFn: () => fetchProjectIssuesBoard(projectId, { status: 'all', page: 1, pageSize: 1 }),
    enabled: isAutopilot,
  });
  const loopsQuery = useQuery({
    queryKey: qk.projectLoops(projectId),
    queryFn: () => fetchProjectLoops(projectId),
    enabled: isAutopilot,
  });
  const counts = boardCountsQuery.data?.counts;
  const teamIdle =
    isAutopilot &&
    !!counts &&
    !!loopsQuery.data &&
    counts.proposed + counts.open + counts.building + counts.review === 0 &&
    !loopsQuery.data.some((loop) => loop.enabled);
  const { wp } = useWorkspacePath();
  const projectRef = query.data?.code ?? projectId;
  const projectKind = query.data?.kind ?? 'repo';

  const gateMode = query.data?.gateMode ?? 'strict';

  const [branchInput, setBranchInput] = useState('');
  const [branchError, setBranchError] = useState('');

  useEffect(() => {
    if (query.data?.defaultBranch) {
      setBranchInput(query.data.defaultBranch);
    }
  }, [query.data?.defaultBranch]);

  function saveBranch() {
    const trimmed = branchInput.trim();
    if (!trimmed) {
      setBranchError('Branch name cannot be empty.');
      return;
    }
    setBranchError('');
    defaultBranchMutation.mutate(trimmed);
  }

  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState('');

  useEffect(() => {
    if (query.data?.name) {
      setNameInput(query.data.name);
    }
  }, [query.data?.name]);

  function saveName() {
    const trimmed = nameInput.trim();
    if (!trimmed) {
      setNameError('Name cannot be empty.');
      return;
    }
    setNameError('');
    renameMutation.mutate(trimmed);
  }

  function setAutonomy(next: ProjectAutonomy) {
    if (!canManage || mutation.isPending || next === autonomy) return;
    mutation.mutate(next);
  }

  function setGateMode(next: GateMode) {
    if (!canManage || gateModeMutation.isPending || next === gateMode) return;
    gateModeMutation.mutate(next);
  }

  if (query.isError) {
    return (
      <p className="text-sm text-ink-faint">Couldn't load the team. Reload to try again.</p>
    );
  }

  return (
    <section aria-label="Team">
      <div className="grid gap-3 sm:grid-cols-2">
        {AGENT_CATALOG.map((agent) => (
            <div key={agent.role} className="card flex flex-col gap-3 p-4">
              <div className="flex min-w-0 items-center gap-2">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-full bg-surface-2 text-ink">
                  {ICONS[agent.role]}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">{agent.name}</p>
                  <p className="text-xs text-ink-faint">{agent.tagline}</p>
                </div>
              </div>
              <p className="text-xs leading-relaxed text-ink-muted">{agent.description}</p>
            </div>
          ))}
      </div>

      <div className="card mt-4 flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-semibold text-ink">Autonomy</p>
          <p className="text-xs text-ink-muted">How much the team does without you.</p>
        </div>
        <div
          role="radiogroup"
          aria-label="Autonomy"
          className="flex flex-col gap-2 sm:flex-row"
        >
          {AUTONOMY_OPTIONS.map((option) => {
            const checked = autonomy === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={checked}
                disabled={!canManage || mutation.isPending || !autonomy}
                onClick={() => setAutonomy(option.value)}
                className={[
                  'flex-1 rounded-md border px-3 py-2 text-left text-xs transition-colors',
                  checked
                    ? 'border-signal bg-signal/10 text-ink'
                    : 'border-ink-faint/30 text-ink-muted',
                  canManage ? 'cursor-pointer' : 'cursor-default opacity-70',
                ].join(' ')}
              >
                <span className="block font-semibold">{option.label}</span>
                <span className="mt-0.5 block leading-relaxed">{option.detail}</span>
              </button>
            );
          })}
        </div>
        {!canManage && (
          <p className="text-xs text-ink-faint">
            Only an owner can change the team&#x2019;s autonomy.
          </p>
        )}
        {teamIdle && (
          <Callout
            tone="info"
            kicker="The team is idle"
            actions={
              <>
                <Link className="btn-secondary btn-sm" to={wp(newIssueHref(projectKind, projectRef))}>
                  New issue
                </Link>
                <Link className="btn-secondary btn-sm" to={wp(`${loopsHref(projectKind, projectRef)}/new`)}>
                  Set up a loop
                </Link>
              </>
            }
          >
            Autopilot builds what&#x2019;s on the board, and the board is empty. File an issue
            to get something built, or set up a loop so the PM regularly reviews the product
            and proposes improvements.
          </Callout>
        )}
      </div>

      <div className="card mt-4 flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-semibold text-ink">Gate Mode</p>
          <p className="text-xs text-ink-muted">What happens when tests keep failing after all automatic retries.</p>
        </div>
        <div
          role="radiogroup"
          aria-label="Gate Mode"
          className="flex flex-col gap-2 sm:flex-row"
        >
          {GATE_MODE_OPTIONS.map((option) => {
            const checked = gateMode === option.value;
            const cls = [
              'flex-1 rounded-md border px-3 py-2 text-left text-xs transition-colors',
              checked ? 'border-signal bg-signal/10 text-ink' : 'border-ink-faint/30 text-ink-muted',
              canManage ? 'cursor-pointer' : 'cursor-default opacity-70',
            ].join(' ');
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={checked}
                disabled={!canManage || gateModeMutation.isPending || !query.data}
                onClick={() => setGateMode(option.value)}
                className={cls}
              >
                <span className="block font-semibold">{option.label}</span>
                <span className="mt-0.5 block leading-relaxed">{option.detail}</span>
              </button>
            );
          })}
        </div>
        {!canManage && (
          <p className="text-xs text-ink-faint">
            Only an owner can change the gate mode.
          </p>
        )}
      </div>

      <div className="card mt-4 flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-semibold text-ink">Display name</p>
          <p className="text-xs text-ink-muted">
            The name shown in the app. Renaming never changes the issue code, the GitHub
            repository, or a prototype's live URL.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            aria-label="Display name"
            value={nameInput}
            maxLength={120}
            onChange={(e) => { setNameInput(e.target.value); setNameError(''); }}
            disabled={!canManage || renameMutation.isPending}
            className="flex-1 rounded-md border border-ink-faint/30 bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-signal disabled:opacity-60"
          />
          <button
            type="button"
            onClick={saveName}
            disabled={!canManage || renameMutation.isPending}
            className="rounded-md border border-ink-faint/30 px-3 py-1.5 text-sm text-ink transition-colors hover:bg-surface-2 disabled:opacity-60"
          >
            {renameMutation.isPending ? 'Saving…' : 'Rename'}
          </button>
        </div>
        {nameError && <p className="text-xs text-red-500">{nameError}</p>}
        {renameMutation.isError && !nameError && (
          <p className="text-xs text-red-500">Couldn't rename. Try again.</p>
        )}
        {renameMutation.isSuccess && !nameError && (
          <p className="text-xs text-green-600">Renamed.</p>
        )}
        {!canManage && (
          <p className="text-xs text-ink-faint">
            Only an owner can rename this project.
          </p>
        )}
      </div>

      <div className="card mt-4 flex flex-col gap-3 p-4">
        <div>
          <p className="text-sm font-semibold text-ink">Base branch</p>
          <p className="text-xs text-ink-muted">The branch all engineer PRs target.</p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={branchInput}
            onChange={(e) => { setBranchInput(e.target.value); setBranchError(''); }}
            disabled={!canManage || defaultBranchMutation.isPending}
            placeholder="main"
            className="flex-1 rounded-md border border-ink-faint/30 bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-signal disabled:opacity-60"
          />
          <button
            type="button"
            onClick={saveBranch}
            disabled={!canManage || defaultBranchMutation.isPending}
            className="rounded-md border border-ink-faint/30 px-3 py-1.5 text-sm text-ink transition-colors hover:bg-surface-2 disabled:opacity-60"
          >
            {defaultBranchMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
        {branchError && <p className="text-xs text-red-500">{branchError}</p>}
        {defaultBranchMutation.isSuccess && !branchError && (
          <p className="text-xs text-green-600">Saved.</p>
        )}
        {!canManage && (
          <p className="text-xs text-ink-faint">
            Only an owner can change the base branch.
          </p>
        )}
      </div>
    </section>
  );
}
